import { createAsyncThunk } from '@reduxjs/toolkit';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type {
  HelpdeskSettings,
  RoutingRule,
  SLAPolicy,
  OperatorScheduleEntry,
} from '../../../types/helpdeskSettingsInterfaces';

export const fetchHelpdeskSettings = createAsyncThunk<
  HelpdeskSettings,
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchSettings', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getSettings();
    // Singleton — API returns array with one item via ViewSet.list
    const data = Array.isArray(response.data) ? response.data[0] : response.data;
    return data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки настроек');
  }
});

export const updateHelpdeskSettings = createAsyncThunk<
  HelpdeskSettings,
  Partial<HelpdeskSettings>,
  { rejectValue: string }
>('helpdeskSettings/updateSettings', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateSettings(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка сохранения настроек');
  }
});

// --- Routing Rules ---
export const fetchRoutingRules = createAsyncThunk<
  RoutingRule[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchRoutingRules', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getRoutingRules();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки правил');
  }
});

export const createRoutingRule = createAsyncThunk<
  RoutingRule,
  Partial<RoutingRule>,
  { rejectValue: string }
>('helpdeskSettings/createRoutingRule', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createRoutingRule(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка создания правила');
  }
});

export const updateRoutingRule = createAsyncThunk<
  RoutingRule,
  { id: number; data: Partial<RoutingRule> },
  { rejectValue: string }
>('helpdeskSettings/updateRoutingRule', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateRoutingRule(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка обновления правила');
  }
});

export const deleteRoutingRule = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteRoutingRule', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteRoutingRule(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления правила');
  }
});

export const reorderRoutingRules = createAsyncThunk<
  number[],
  number[],
  { rejectValue: string }
>('helpdeskSettings/reorderRoutingRules', async (order, thunkApi) => {
  try {
    await helpdeskSettingsApi.reorderRoutingRules(order);
    return order;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка сортировки');
  }
});

// --- SLA Policies ---
export const fetchSLAPolicies = createAsyncThunk<
  SLAPolicy[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchSLAPolicies', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getSLAPolicies();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки SLA');
  }
});

export const createSLAPolicy = createAsyncThunk<
  SLAPolicy,
  Partial<SLAPolicy>,
  { rejectValue: string }
>('helpdeskSettings/createSLAPolicy', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createSLAPolicy(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка создания SLA');
  }
});

export const updateSLAPolicy = createAsyncThunk<
  SLAPolicy,
  { id: number; data: Partial<SLAPolicy> },
  { rejectValue: string }
>('helpdeskSettings/updateSLAPolicy', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateSLAPolicy(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка обновления SLA');
  }
});

export const deleteSLAPolicy = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteSLAPolicy', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteSLAPolicy(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления SLA');
  }
});

// --- Schedules ---
export const fetchSchedules = createAsyncThunk<
  OperatorScheduleEntry[],
  number | undefined,
  { rejectValue: string }
>('helpdeskSettings/fetchSchedules', async (operatorId, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getSchedules(operatorId);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки расписаний');
  }
});

export const updateSchedule = createAsyncThunk<
  OperatorScheduleEntry,
  { id: number; data: Partial<OperatorScheduleEntry> },
  { rejectValue: string }
>('helpdeskSettings/updateSchedule', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateSchedule(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка обновления расписания');
  }
});

export const createSchedule = createAsyncThunk<
  OperatorScheduleEntry,
  Partial<OperatorScheduleEntry>,
  { rejectValue: string }
>('helpdeskSettings/createSchedule', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createSchedule(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка создания расписания');
  }
});
